import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { SERVICES, TESTIMONIALS } from "@/data/services";
import { VytaText } from "@/components/Vyta";
import Seo from "@/components/Seo";

const SITE = "https://www.scalexa-group.com";

export default function ServicePage() {
    useReveal();
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);

    const jsonLd = useMemo(() => {
        if (!service) return null;
        return {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Service",
                    name: service.name,
                    description: service.seo.description,
                    url: `${SITE}/services/${service.slug}`,
                    provider: {
                        "@type": "Organization",
                        name: "Scalexa Group",
                        url: `${SITE}/`,
                    },
                    areaServed: ["ES", "MX"],
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE}/` },
                        { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE}/services` },
                        { "@type": "ListItem", position: 3, name: service.name, item: `${SITE}/services/${service.slug}` },
                    ],
                },
            ],
        };
    }, [service]);

    if (!service) return <Navigate to="/services" replace />;

    const testimonial =
        service.testimonial != null ? TESTIMONIALS[service.testimonial] : null;
    const others = SERVICES.filter((s) => s.slug !== service.slug);

    return (
        <div data-testid="service-page">
            <Seo
                title={service.seo.title}
                description={service.seo.description}
                path={`/services/${service.slug}`}
                jsonLd={jsonLd}
            />

            {/* HERO */}
            <section className="container-x pt-16 pb-16 lg:pt-24 lg:pb-20 border-b border-brand-line">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono">Servicio {service.n} · Scalexa Group</div>
                        <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-brand-navy">
                            {service.name}
                        </h1>
                        {service.definition && (
                            <p className="mt-6 inline-block border border-brand-line bg-brand-soft px-4 py-3 text-sm text-brand-navy">
                                {service.definition}
                            </p>
                        )}
                        <p className="mt-6 max-w-2xl text-lg lg:text-xl text-brand-gray leading-relaxed">
                            {service.main}
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to={`/contact?interes=${service.slug}`}
                                data-testid="service-cta-main"
                                className="btn-primary"
                            >
                                {service.cta} <ArrowRight size={16} />
                            </Link>
                            <Link to="/services" data-testid="service-cta-all" className="btn-ghost">
                                Ver todos los servicios
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* PARA QUIÉN ES */}
            <section className="container-x py-16 lg:py-24 border-b border-brand-line" data-testid="service-audience">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-3">
                        <div className="label-mono">Para quién es</div>
                    </div>
                    <div className="lg:col-span-8">
                        <p className="font-heading text-2xl lg:text-3xl leading-[1.3] text-brand-navy">
                            {service.audience}
                        </p>
                    </div>
                </div>
            </section>

            {/* QUÉ HACEMOS */}
            <section className="container-x py-16 lg:py-24" data-testid="service-whatwedo">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 reveal">
                    <div className="lg:col-span-3">
                        <div className="label-mono">Qué hacemos</div>
                    </div>
                    <div className="lg:col-span-8">
                        <h2 className="font-heading text-3xl lg:text-4xl leading-tight text-brand-navy">
                            {service.modalities
                                ? "Dos modalidades dentro de la misma línea."
                                : "El trabajo, en concreto."}
                        </h2>
                    </div>
                </div>

                {service.modalities ? (
                    <div className="reveal">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
                            {service.modalities.map((m, i) => (
                                <div key={i} className="bg-white p-8 lg:p-10" data-testid={`service-modality-${i}`}>
                                    <div className="label-mono">{String.fromCharCode(65 + i)}</div>
                                    <h3 className="font-heading text-2xl mt-3 text-brand-navy">{m.title}</h3>
                                    <ul className="mt-6 space-y-3">
                                        {m.items.map((it, ii) => (
                                            <li key={ii} className="flex items-start gap-3">
                                                <Check className="text-brand-electric mt-1 shrink-0" size={16} />
                                                <span className="text-brand-gray">{it}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-brand-gray max-w-2xl leading-relaxed">
                            {service.modalitiesNote}
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-brand-line border-t border-b border-brand-line reveal">
                        {service.whatWeDo.map((it, i) => (
                            <li
                                key={i}
                                className="py-5 flex items-start gap-4"
                                data-testid={`service-whatwedo-item-${i}`}
                            >
                                <Check className="text-brand-electric mt-1 shrink-0" size={16} />
                                <span className="text-brand-navy text-lg">{it}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* BLOQUE DIFERENCIAL (colaboración / metodología) */}
            {service.extra && (
                <section className="container-x pb-16 lg:pb-24" data-testid="service-extra">
                    <div className="border border-brand-line bg-brand-soft p-8 lg:p-12 reveal">
                        <div className="label-mono">En la práctica</div>
                        <h2 className="font-heading text-2xl lg:text-3xl mt-4 text-brand-navy leading-tight">
                            {service.extra.title}
                        </h2>
                        <p className="mt-4 text-brand-gray leading-relaxed max-w-3xl">
                            <VytaText text={service.extra.body} />
                        </p>
                    </div>
                </section>
            )}

            {/* ENTREGABLES */}
            <section className="border-t border-brand-line bg-white" data-testid="service-deliverables">
                <div className="container-x py-16 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 reveal">
                        <div className="lg:col-span-3">
                            <div className="label-mono">Qué recibe el cliente</div>
                        </div>
                        <div className="lg:col-span-8">
                            <h2 className="font-heading text-3xl lg:text-4xl leading-tight text-brand-navy">
                                Entregables concretos, sin humo.
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line reveal">
                        {service.deliverables.map((d, i) => (
                            <div key={i} className="bg-white p-8" data-testid={`service-deliverable-${i}`}>
                                <div className="label-mono">{String(i + 1).padStart(2, "0")}</div>
                                <p className="mt-3 text-brand-navy leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CÓMO TRABAJAMOS */}
            <section className="border-t border-brand-line" data-testid="service-how">
                <div className="container-x py-16 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 reveal">
                        <div className="lg:col-span-3">
                            <div className="label-mono">Cómo trabajamos</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-line border border-brand-line reveal">
                        {service.howWeWork.map((s, i) => (
                            <div key={i} className="bg-white p-8" data-testid={`service-step-${i}`}>
                                <div className="label-mono">{String(i + 1).padStart(2, "0")}</div>
                                <h3 className="font-heading text-xl mt-4 text-brand-navy">{s.t}</h3>
                                <p className="mt-3 text-sm text-brand-gray leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 reveal">
                        <p className="text-brand-navy">
                            <span className="label-mono mr-3">Modalidad</span>
                            {service.modality}
                        </p>
                    </div>
                </div>
            </section>

            {/* TESTIMONIO RELACIONADO */}
            {testimonial && (
                <section className="border-t border-brand-line" data-testid="service-testimonial">
                    <div className="container-x py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                            <div className="lg:col-span-3">
                                <div className="label-mono">Casos y experiencia</div>
                            </div>
                            <div className="lg:col-span-8">
                                <figure>
                                    <blockquote className="font-heading text-2xl lg:text-3xl leading-[1.25] text-brand-navy">
                                        "{testimonial.q}"
                                    </blockquote>
                                    <figcaption className="mt-5 label-mono !text-brand-gray">
                                        <VytaText text={testimonial.a} />
                                    </figcaption>
                                </figure>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA FINAL */}
            <section className="border-t border-brand-line bg-brand-navy text-white" data-testid="service-cta">
                <div className="container-x py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8 reveal">
                        <div className="label-mono !text-white/60">Siguiente paso</div>
                        <h2 className="font-heading text-3xl lg:text-5xl mt-6 leading-[1.05] text-white">
                            Cuéntanos tu situación y te diremos con franqueza{" "}
                            <span className="italic">si podemos ayudarte.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right reveal">
                        <Link
                            to={`/contact?interes=${service.slug}`}
                            data-testid="service-cta-bottom"
                            className="btn-primary !bg-white !text-brand-navy"
                        >
                            {service.cta} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* OTROS SERVICIOS */}
            <section className="container-x py-16 lg:py-24" data-testid="service-others">
                <div className="flex items-center justify-between mb-8 reveal">
                    <div className="label-mono">Explora también</div>
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-2 text-sm text-brand-electric hover:text-brand-navy transition-colors"
                        data-testid="service-back-all"
                    >
                        <ArrowLeft size={14} /> Todos los servicios
                    </Link>
                </div>
                <div className="border-t border-brand-line reveal">
                    {others.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`service-other-${s.slug}`}
                            className="grid grid-cols-12 gap-4 items-center py-5 border-b border-brand-line group hover:bg-brand-soft transition-colors"
                        >
                            <div className="col-span-2 lg:col-span-1 label-mono">{s.n}</div>
                            <div className="col-span-9 lg:col-span-10 font-heading text-lg lg:text-xl text-brand-navy">
                                {s.name}
                            </div>
                            <div className="col-span-1 flex justify-end">
                                <ArrowRight
                                    size={18}
                                    className="text-brand-navy opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                                />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
